import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { take } from 'rxjs/operators';

import { AuthLayoutComponent } from '../../shared/layouts/auth-layout.component';
import { AuthService } from '../../core/services/auth.service';
import { CartFacade } from '../../core/store/cart/cart.facade';
import { ToastService } from '../../shared/services/toast.service';
import { HeaderService } from '../../shared/services/header.service';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { UserFacade } from '../../core/store/user/user.facade';
import { FixaResource, SelectContractRequest } from '../../core/models/login.model';
import { formatFixaContractDisplay } from '../../core/utils/fixa-contract-display.util';
import { UtmPreservationService } from '../../core/services/utm-preservation.service';
import { UserService } from '../../core/services/user.service';
import { applySelectContractToSession } from '../../core/utils/select-contract-response.util';
import { constructFinalRedirectWithToken } from '../../core/utils/external-channel-access.util';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';

@Component({
  selector: 'umkt-select-fixa-contract',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    MatIconModule,
    AuthLayoutComponent,
    ConfirmModalComponent,
    LoadingComponent,
    I18nPipe,
  ],
  templateUrl: './select-fixa-contract.component.html',
  styleUrls: [
    '../../../assets/style/login-register.component.scss',
    './select-fixa-contract.component.scss'
  ],
})
export class SelectFixaContractComponent implements OnInit {
  
  searchForm!: FormGroup;
  searchTerm: string = '';

  contracts: FixaResource[] = [];
  filteredContracts: FixaResource[] = [];
  selectedContract: FixaResource | null = null;
  loading = false;
  showLogoutConfirm = false;
  companyName: string = '';

  get hasMoreThanFourItems(): boolean {
    return this.filteredContracts.length > 4;
  }
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private cartFacade = inject(CartFacade);
  private toastService = inject(ToastService);
  private headerService = inject(HeaderService);
  private userFacade = inject(UserFacade);
  private utmPreservationService = inject(UtmPreservationService);


  ngOnInit(): void {
    this.initializeForm();
    this.loadContractData();
  }

  private initializeForm(): void {
    this.searchForm = this.fb.group({
      search: ['']
    });
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.filteredContracts = [...this.contracts];
    this.selectedContract = null;
  }

  onSearchChange(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.searchTerm = inputValue;
    this.filterContracts(inputValue);
    
    // Se houver apenas um resultado e corresponder ao termo de busca, selecionar automaticamente
    if (this.filteredContracts.length === 1 && this.isContractSearch(inputValue)) {
      this.selectedContract = this.filteredContracts[0];
    } else {
      this.selectedContract = null;
    }
  }

  private isContractSearch(searchTerm: string): boolean {
    const cleanTerm = searchTerm.replace(/[^\d]/g, '');
    return cleanTerm.length >= 4; // Pelo menos 4 dígitos para buscar por customerAccountId
  }

  private filterContracts(searchTerm: string): void {
    if (!searchTerm || searchTerm.trim() === '') {
      this.filteredContracts = [...this.contracts];
      return;
    }

    const cleanSearch = searchTerm.toLowerCase().trim();
    
    this.filteredContracts = this.contracts.filter(contract => {
      const customerAccountId = contract.customerAccountId.toLowerCase();
      const operatorCode = String(contract.operatorCode ?? '').toLowerCase();
      const status = contract.status.toLowerCase();
      const sourceSystem = contract.sourceSystemDescription.toLowerCase();
      const displayLabel = formatFixaContractDisplay(contract).toLowerCase();

      const customerMatch = customerAccountId.includes(cleanSearch);
      const operatorMatch = operatorCode.includes(cleanSearch);
      const statusMatch = status.includes(cleanSearch);
      const sourceMatch = sourceSystem.includes(cleanSearch);
      const displayMatch = displayLabel.includes(cleanSearch);

      return customerMatch || operatorMatch || statusMatch || sourceMatch || displayMatch;
    });
  }

  private loadContractData(): void {
    this.loading = true;
    
    this.userFacade.getLoginData().pipe(take(1)).subscribe(loginData => {
      if (!loginData || !loginData.resources || loginData.resources.length === 0) {
        this.loading = false;
        this.router.navigate(['/login']);
        return;
      }

      // Verificar se são recursos da fixa (objetos)
      const firstResource = loginData.resources[0];
      const isFixaMode = firstResource && typeof firstResource === 'object' && 'customerAccountId' in firstResource;
      
      if (!isFixaMode) {
        this.loading = false;
        this.router.navigate(['/login']);
        return;
      }

      // Garantir que resources são objetos FixaResource
      const allContracts = loginData.resources as FixaResource[];
      
      // Filtrar contratos ativos (ACTIVE ou CONECTADO, conforme retorno do authPing)
      const activeStatuses = ['ACTIVE', 'CONECTADO'];
      this.contracts = allContracts.filter(contract =>
        activeStatuses.includes(contract.status?.toUpperCase?.() ?? '')
      );
      
      if (this.contracts.length === 0) {
        this.toastService.showError(
          'Nenhum contrato ativo disponível. Entre em contato com o suporte.',
          5000,
          'top-center'
        );
        this.loading = false;
        this.router.navigate(['/login']);
        return;
      }
      
      this.filteredContracts = [...this.contracts];
      this.companyName = loginData.companyName || '';
      
      this.loading = false;
    });
  }

  selectContract(contract: FixaResource): void {
    this.selectedContract = contract;
    this.loading = true;

    this.userFacade.setSelectedContract(contract);

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('umkt_selected_cnpj', contract.customerAccountId);
    }

    // Obter dados do login para construir o request
    this.userFacade.getLoginData().pipe(take(1)).subscribe(loginData => {
      if (!loginData) {
        this.loading = false;
        this.toastService.showError(
          'Erro ao obter dados da sessão. Por favor, faça login novamente.',
          5000,
          'top-center'
        );
        this.router.navigate(['/login']);
        return;
      }

      const request: SelectContractRequest = {
        operatorCode: contract.operatorCode,
        customerAccountId: contract.customerAccountId,
        document: loginData.document || '',
        sessionId: loginData.sessionId || ''
      };

      this.userService.selectContract(request).pipe(take(1)).subscribe({
        next: (response) => {
          if (response) {
            applySelectContractToSession(response, this.authService, this.userFacade);
            const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
              ...(this.route.snapshot?.queryParams || {}),
            });
            const redirect = constructFinalRedirectWithToken();
            if (redirect) {
              this.router.navigate(redirect.commands, {
                queryParams: { ...queryParams, ...redirect.queryParams },
              });
            } else {
              this.router.navigate(['/'], { queryParams });
            }
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.toastService.showError(
            'Erro ao selecionar contrato. Tente novamente.',
            5000,
            'top-center'
          );
        },
      });
    });
  }

  openLogoutConfirm(): void {
    this.showLogoutConfirm = true;
  }

  handleLogoutConfirm(): void {
    this.showLogoutConfirm = false;
    this.logout();
  }

  handleLogoutCancel(): void {
    this.showLogoutConfirm = false;
  }

  private logout(): void {
    this.authService.logout();
    
    this.cartFacade.clearCart();
    
    this.headerService.resetActiveTopNavItem();
    
    this.toastService.showSuccess(
      'Sessão encerrada com sucesso.',
      3000,
      'top-center'
    );
    
    this.router.navigate(['/login']);
  }

  trackByContract(index: number, contract: FixaResource): string {
    return contract.customerAccountId;
  }

  fixaContractDisplayLabel(contract: FixaResource): string {
    return formatFixaContractDisplay(contract);
  }

  getStatusLabel(status: string): string {
    switch (status?.toUpperCase?.()) {
      case 'ACTIVE':
      case 'CONECTADO':
        return 'Ativo';
      case 'SUSPENDED':
        return 'Suspenso';
      case 'INACTIVE':
        return 'Inativo';
      default:
        return status || '';
    }
  }

  getStatusClass(status: string): string {
    const upper = status?.toUpperCase?.();
    if (upper === 'ACTIVE' || upper === 'CONECTADO') return 'status-active';
    if (upper === 'SUSPENDED') return 'status-suspended';
    if (upper === 'INACTIVE') return 'status-inactive';
    return '';
  }
}

